/**
 * CapitalQuoteBuilder
 * -------------------
 * Modal form for creating / editing a capital-project contracted quote.
 * Mirrors the Sourcing Desk "Build Quote" UX — but for large capital
 * projects with core-system specs, timelines, payment milestones, etc.
 *
 * On save, the record is persisted to Mongo (`capital_project_quotes`)
 * and the parent list refetches.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, X, Eye, ChevronDown, ChevronRight, Maximize2, Minimize2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

const emptyQuote = () => ({
  quote_number: "",
  title: "",
  subtitle: "",
  kicker: "Contracted Quote",
  customer_name: "",
  customer_site: "",
  customer_contact: "",
  customer_email: "",
  partner_brand: "",
  partner_role: "",
  issue_date: new Date().toISOString().slice(0, 10),
  valid_days: 60,
  target_complete: "",
  fob_origin: "",
  prime_contractor_line: "Sterile Mate LLC · MBE Certified",
  system_oem_line: "",
  executive_summary: "",
  partners: [],
  owner_furnished_note: "",
  design_assumptions: [],
  core_system_headline: "",
  core_system_name: "",
  core_system_description: "",
  core_system_image_url: "",
  core_system_specs: [],
  core_system_features: [],
  core_system_certifications: [],
  line_items: [{ section: "A · Equipment", description: "", qty: 1, unit_price: null, extended: 0 }],
  bundle_discount: 0,
  bundle_discount_note: "",
  payment_terms_note:
    "Orders require the 40% down payment with the executed PO. The second 40% is billed against confirmed equipment arrival. The final 20% is billed at successful start-up or 90 days after equipment arrival, whichever comes first.",
  payment_schedule: [
    { label: "Down Payment with PO", percent: 40, amount: 0, when: "due upon Owner PO issuance" },
    { label: "Equipment Arrival", percent: 40, amount: 0, when: "due upon confirmed delivery of equipment" },
    { label: "Final Balance", percent: 20, amount: 0, when: "due at start-up OR 90 days after arrival, whichever comes first" },
  ],
  timeline: [],
  warranty_summary: "",
  preventive_maintenance_summary: "",
  owner_responsibilities: [],
  acceptance_paragraph:
    "This contracted quote, signed and dated below by an authorized representative of the Owner, together with the accompanying PO, constitutes the mutually binding agreement between the parties for the work described herein.",
  signer_name: "George Broughton III",
  signer_title: "Founder & CEO",
  signer_date: "",
  status: "draft",
});

const SECTIONS = [
  { id: "header",   label: "Header & Customer" },
  { id: "scope",    label: "Scope, Assumptions & Owner Responsibilities" },
  { id: "system",   label: "Core System & Specifications" },
  { id: "items",    label: "Line Items & Pricing" },
  { id: "payment",  label: "Payment Schedule & Terms" },
  { id: "timeline", label: "Timeline, Warranty & Signature" },
];

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CapitalQuoteBuilder({ open, editingId, token, onClose, onSaved, pageMode = false }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [q, setQ] = useState(emptyQuote());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Collapsible sections (replaces the old tab strip). Everything is open by
  // default on the full-page screen so the whole quote reads top-to-bottom.
  const [openSections, setOpenSections] = useState(() => new Set(SECTIONS.map((x) => x.id)));
  const [wide, setWide] = useState(false);
  const toggleSection = (id) => setOpenSections((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const expandAll = () => setOpenSections(new Set(SECTIONS.map((x) => x.id)));
  const collapseAll = () => setOpenSections(new Set());

  useEffect(() => {
    if (!open) return;
    if (!editingId) {
      setQ(emptyQuote());
      expandAll();
      return;
    }
    // Load existing quote for edit
    setLoading(true);
    axios.get(`${API}/api/admin/capital-quotes/${editingId}`, { headers })
      .then((r) => setQ(r.data))
      .catch(() => toast.error("Failed to load capital quote"))
      .finally(() => setLoading(false));
  }, [open, editingId, headers]);

  // Live totals
  const subtotal = useMemo(() => {
    return (q.line_items || []).reduce((s, li) => {
      const unit = Number(li.unit_price);
      if (li.unit_price !== null && li.unit_price !== "" && !isNaN(unit)) {
        return s + unit * (Number(li.qty) || 1);
      }
      const n = Number(li.extended);
      return isNaN(n) ? s : s + n;
    }, 0);
  }, [q.line_items]);
  const grand = subtotal - Number(q.bundle_discount || 0);

  const set = (k, v) => setQ((p) => ({ ...p, [k]: v }));
  const setLine = (i, k, v) => setQ((p) => ({ ...p, line_items: p.line_items.map((li, idx) => idx === i ? { ...li, [k]: v } : li) }));
  const addLine = () => setQ((p) => ({ ...p, line_items: [...p.line_items, { section: p.line_items.at(-1)?.section || "A · Equipment", description: "", qty: 1, unit_price: null, extended: 0 }] }));
  const removeLine = (i) => setQ((p) => ({ ...p, line_items: p.line_items.filter((_, idx) => idx !== i) }));

  const addAssumption = () => set("design_assumptions", [...(q.design_assumptions || []), ""]);
  const setAssumption = (i, v) => set("design_assumptions", q.design_assumptions.map((x, idx) => idx === i ? v : x));
  const removeAssumption = (i) => set("design_assumptions", q.design_assumptions.filter((_, idx) => idx !== i));

  const addResp = () => set("owner_responsibilities", [...(q.owner_responsibilities || []), ""]);
  const setResp = (i, v) => set("owner_responsibilities", q.owner_responsibilities.map((x, idx) => idx === i ? v : x));
  const removeResp = (i) => set("owner_responsibilities", q.owner_responsibilities.filter((_, idx) => idx !== i));

  const addFeature = () => set("core_system_features", [...(q.core_system_features || []), ""]);
  const setFeature = (i, v) => set("core_system_features", q.core_system_features.map((x, idx) => idx === i ? v : x));
  const removeFeature = (i) => set("core_system_features", q.core_system_features.filter((_, idx) => idx !== i));

  const addCert = () => set("core_system_certifications", [...(q.core_system_certifications || []), ""]);
  const setCert = (i, v) => set("core_system_certifications", q.core_system_certifications.map((x, idx) => idx === i ? v : x));
  const removeCert = (i) => set("core_system_certifications", q.core_system_certifications.filter((_, idx) => idx !== i));

  const addSpec = () => set("core_system_specs", [...(q.core_system_specs || []), { label: "", value: "" }]);
  const setSpec = (i, k, v) => set("core_system_specs", q.core_system_specs.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const removeSpec = (i) => set("core_system_specs", q.core_system_specs.filter((_, idx) => idx !== i));

  const addTimeline = () => set("timeline", [...(q.timeline || []), { when: "", description: "" }]);
  const setTimelineRow = (i, k, v) => set("timeline", q.timeline.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const removeTimeline = (i) => set("timeline", q.timeline.filter((_, idx) => idx !== i));

  const addPayment = () => set("payment_schedule", [...(q.payment_schedule || []), { label: "", percent: null, amount: 0, when: "" }]);
  const setPayment = (i, k, v) => set("payment_schedule", q.payment_schedule.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const removePayment = (i) => set("payment_schedule", q.payment_schedule.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!q.title || !q.customer_name || !q.quote_number) {
      toast.error("Title, Customer, and Quote No. are required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/api/admin/capital-quotes/${editingId}`, q, { headers });
      } else {
        await axios.post(`${API}/api/admin/capital-quotes`, q, { headers });
      }
      toast.success("Capital quote saved");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save capital quote");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Shell pageMode={pageMode} wide={wide}>
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2 border-b border-slate-200 px-4 sm:px-5 py-3 bg-slate-50 rounded-t-xl sticky top-0 z-20">
          <div className="flex items-center gap-3">
            {pageMode && (
              <Button variant="ghost" size="icon" onClick={onClose} title="Back to Proposals" data-testid="cqb-back">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div>
              <p className="text-[10px] font-black uppercase tracking-wider text-cyan-700">Capital Project Quote</p>
              <h2 className="text-base sm:text-lg font-extrabold text-slate-900">{editingId ? "Edit Contracted Quote" : "New Contracted Quote"}</h2>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="text-right">
              <p className="text-[10px] text-slate-500">Contract Price</p>
              <p className="text-lg font-black text-slate-900">{fmt$(grand)}</p>
            </div>
            {pageMode ? (
              <>
                <Button variant="outline" size="sm" onClick={() => setWide((w) => !w)} title={wide ? "Use comfortable width" : "Use full screen width"} data-testid="cqb-width-toggle">
                  {wide ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </Button>
                <Button onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700" size="sm" data-testid="cqb-save-top">
                  {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-1" />Save</>}
                </Button>
              </>
            ) : (
              <Button variant="ghost" size="icon" onClick={onClose} data-testid="cqb-close"><X className="w-4 h-4" /></Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Loading…</div>
        ) : (
          <div className="p-5">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <p className="text-[11px] font-black uppercase tracking-wider text-slate-500">
                {openSections.size} of {SECTIONS.length} sections open
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={expandAll} data-testid="cqb-expand-all">Expand all</Button>
                <Button variant="outline" size="sm" onClick={collapseAll} data-testid="cqb-collapse-all">Collapse all</Button>
              </div>
            </div>

              {/* ---------- HEADER ---------- */}
              <Section id="header" title="Header & Customer" open={openSections.has("header")} onToggle={toggleSection}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Quote No.*"><Input value={q.quote_number} onChange={(e) => set("quote_number", e.target.value)} placeholder="SM-XX-000-2026-001" data-testid="cqb-quote-no" /></Field>
                  <Field label="Status"><select className="w-full h-10 border rounded px-3 text-sm" value={q.status} onChange={(e) => set("status", e.target.value)}><option value="draft">Draft</option><option value="sent">Sent</option><option value="accepted">Accepted</option><option value="declined">Declined</option></select></Field>
                  <Field label="Project Title*"><Input value={q.title} onChange={(e) => set("title", e.target.value)} placeholder="16-Bay Dialysis Water Treatment System" data-testid="cqb-title" /></Field>
                  <Field label="Subtitle"><Input value={q.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Harris Health System · New John O'Quinn Hospital" /></Field>
                  <Field label="Customer*"><Input value={q.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Harris Health System" data-testid="cqb-customer" /></Field>
                  <Field label="Site"><Input value={q.customer_site} onChange={(e) => set("customer_site", e.target.value)} placeholder="John O'Quinn Hospital (Houston, TX)" /></Field>
                  <Field label="Customer contact"><Input value={q.customer_contact} onChange={(e) => set("customer_contact", e.target.value)} placeholder="Name" /></Field>
                  <Field label="Customer email"><Input value={q.customer_email} onChange={(e) => set("customer_email", e.target.value)} placeholder="buyer@harrishealth.org" /></Field>
                  <Field label="Co-brand partner name"><Input value={q.partner_brand} onChange={(e) => set("partner_brand", e.target.value)} placeholder="NELSON ENVIRONMENTAL TECHNOLOGIES" /></Field>
                  <Field label="Partner role"><Input value={q.partner_role} onChange={(e) => set("partner_role", e.target.value)} placeholder="Equipment & Service Partner" /></Field>
                  <Field label="Issue date"><Input type="date" value={q.issue_date} onChange={(e) => set("issue_date", e.target.value)} /></Field>
                  <Field label="Valid days"><Input type="number" value={q.valid_days} onChange={(e) => set("valid_days", Number(e.target.value))} /></Field>
                  <Field label="Target completion"><Input value={q.target_complete} onChange={(e) => set("target_complete", e.target.value)} placeholder="February 2027" /></Field>
                  <Field label="FOB origin"><Input value={q.fob_origin} onChange={(e) => set("fob_origin", e.target.value)} placeholder="Dayton, OH" /></Field>
                  <Field label="Prime contractor line" className="sm:col-span-2"><Input value={q.prime_contractor_line} onChange={(e) => set("prime_contractor_line", e.target.value)} /></Field>
                  <Field label="System OEM line" className="sm:col-span-2"><Input value={q.system_oem_line} onChange={(e) => set("system_oem_line", e.target.value)} placeholder="AmeriWater (FDA 510(k)-cleared)" /></Field>
                </div>
              </Section>

              {/* ---------- SCOPE ---------- */}
              <Section id="scope" title="Scope, Assumptions & Owner Responsibilities" open={openSections.has("scope")} onToggle={toggleSection}>
                <Field label="Executive summary (HTML allowed)"><Textarea rows={6} value={q.executive_summary} onChange={(e) => set("executive_summary", e.target.value)} /></Field>
                <Field label="Owner-furnished / existing infrastructure note"><Textarea rows={3} value={q.owner_furnished_note} onChange={(e) => set("owner_furnished_note", e.target.value)} /></Field>

                <div>
                  <div className="flex justify-between items-center mb-1"><p className="text-xs font-black uppercase tracking-wider text-slate-600">Design Assumptions</p><Button size="sm" variant="outline" onClick={addAssumption}><Plus className="w-3 h-3 mr-1" />Add</Button></div>
                  {(q.design_assumptions || []).map((a, i) => (
                    <div key={i} className="flex gap-2 mb-1"><Input value={a} onChange={(e) => setAssumption(i, e.target.value)} /><Button size="icon" variant="ghost" onClick={() => removeAssumption(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div>
                  ))}
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1"><p className="text-xs font-black uppercase tracking-wider text-slate-600">Owner Responsibilities</p><Button size="sm" variant="outline" onClick={addResp}><Plus className="w-3 h-3 mr-1" />Add</Button></div>
                  {(q.owner_responsibilities || []).map((a, i) => (
                    <div key={i} className="flex gap-2 mb-1"><Input value={a} onChange={(e) => setResp(i, e.target.value)} /><Button size="icon" variant="ghost" onClick={() => removeResp(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div>
                  ))}
                </div>
              </Section>

              {/* ---------- CORE SYSTEM ---------- */}
              <Section id="system" title="Core System & Specifications" open={openSections.has("system")} onToggle={toggleSection}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Headline"><Input value={q.core_system_headline} onChange={(e) => set("core_system_headline", e.target.value)} placeholder="Heat-Disinfect RO · 2-Membrane" /></Field>
                  <Field label="System name"><Input value={q.core_system_name} onChange={(e) => set("core_system_name", e.target.value)} placeholder="AmeriWater MediQA HD-RO — 12,960 GPD" /></Field>
                  <Field label="Image URL" className="sm:col-span-2"><Input value={q.core_system_image_url} onChange={(e) => set("core_system_image_url", e.target.value)} placeholder="/api/static/email_assets/system.png" /></Field>
                  <Field label="Description (HTML allowed)" className="sm:col-span-2"><Textarea rows={4} value={q.core_system_description} onChange={(e) => set("core_system_description", e.target.value)} /></Field>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1"><p className="text-xs font-black uppercase tracking-wider text-slate-600">Specifications</p><Button size="sm" variant="outline" onClick={addSpec}><Plus className="w-3 h-3 mr-1" />Add row</Button></div>
                  {(q.core_system_specs || []).map((s, i) => (
                    <div key={i} className="grid grid-cols-[180px_1fr_40px] gap-2 mb-1">
                      <Input value={s.label} onChange={(e) => setSpec(i, "label", e.target.value)} placeholder="Model" />
                      <Input value={s.value} onChange={(e) => setSpec(i, "value", e.target.value)} placeholder="MediQA · Heat-Disinfect Single-Pass RO" />
                      <Button size="icon" variant="ghost" onClick={() => removeSpec(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  ))}
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1"><p className="text-xs font-black uppercase tracking-wider text-slate-600">Key Features</p><Button size="sm" variant="outline" onClick={addFeature}><Plus className="w-3 h-3 mr-1" />Add</Button></div>
                  {(q.core_system_features || []).map((f, i) => (
                    <div key={i} className="flex gap-2 mb-1"><Input value={f} onChange={(e) => setFeature(i, e.target.value)} /><Button size="icon" variant="ghost" onClick={() => removeFeature(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div>
                  ))}
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1"><p className="text-xs font-black uppercase tracking-wider text-slate-600">Certifications (pills)</p><Button size="sm" variant="outline" onClick={addCert}><Plus className="w-3 h-3 mr-1" />Add</Button></div>
                  {(q.core_system_certifications || []).map((c, i) => (
                    <div key={i} className="flex gap-2 mb-1"><Input value={c} onChange={(e) => setCert(i, e.target.value)} placeholder="FDA 510(k) Cleared" /><Button size="icon" variant="ghost" onClick={() => removeCert(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div>
                  ))}
                </div>
              </Section>

              {/* ---------- LINE ITEMS ---------- */}
              <Section id="items" title="Line Items & Pricing" open={openSections.has("items")} onToggle={toggleSection}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100 text-[10px] font-black uppercase tracking-wider text-slate-600">
                        <th className="text-left px-2 py-2">Section</th>
                        <th className="text-left px-2 py-2">Description (HTML)</th>
                        <th className="text-center px-2 py-2 w-16">Qty</th>
                        <th className="text-right px-2 py-2 w-28">Unit price</th>
                        <th className="text-right px-2 py-2 w-32">Extended</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(q.line_items || []).map((li, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="p-1"><Input value={li.section} onChange={(e) => setLine(i, "section", e.target.value)} placeholder="A · Equipment" /></td>
                          <td className="p-1"><Input value={li.description} onChange={(e) => setLine(i, "description", e.target.value)} placeholder="Item description" /></td>
                          <td className="p-1"><Input type="number" value={li.qty} onChange={(e) => setLine(i, "qty", Number(e.target.value))} className="text-center" /></td>
                          <td className="p-1"><Input value={li.unit_price ?? ""} onChange={(e) => setLine(i, "unit_price", e.target.value === "" ? null : Number(e.target.value))} placeholder="unit $" className="text-right" data-testid={`cqb-unit-${i}`} /></td>
                          <td className="p-1"><Input value={li.unit_price !== null && li.unit_price !== undefined && li.unit_price !== "" ? fmt$(Number(li.unit_price) * (Number(li.qty) || 1)) : li.extended} disabled={li.unit_price !== null && li.unit_price !== undefined && li.unit_price !== ""} onChange={(e) => setLine(i, "extended", isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value))} placeholder="0.00 or 'Included'" className="text-right" /></td>
                          <td className="p-1 text-center"><Button size="icon" variant="ghost" onClick={() => removeLine(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button size="sm" variant="outline" onClick={addLine} data-testid="cqb-add-line"><Plus className="w-3 h-3 mr-1" />Add line</Button>

                <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-200">
                  <Field label="Bundle discount ($)"><Input type="number" step="0.01" value={q.bundle_discount} onChange={(e) => set("bundle_discount", Number(e.target.value))} /></Field>
                  <Field label="Bundle discount note"><Input value={q.bundle_discount_note} onChange={(e) => set("bundle_discount_note", e.target.value)} /></Field>
                </div>

                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 flex justify-between text-sm">
                  <span>Subtotal</span><span className="font-mono">{fmt$(subtotal)}</span>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-300 flex justify-between text-sm">
                  <span>− Bundle discount</span><span className="font-mono text-emerald-700">− {fmt$(q.bundle_discount || 0)}</span>
                </div>
                <div className="bg-slate-900 rounded-lg p-3 flex justify-between text-white text-base font-black">
                  <span>Total Contract Price</span><span className="font-mono">{fmt$(grand)}</span>
                </div>
              </Section>

              {/* ---------- PAYMENT ---------- */}
              <Section id="payment" title="Payment Schedule & Terms" open={openSections.has("payment")} onToggle={toggleSection}>
                <Field label="Payment terms narrative"><Textarea rows={4} value={q.payment_terms_note} onChange={(e) => set("payment_terms_note", e.target.value)} /></Field>

                <div>
                  <div className="flex justify-between items-center mb-1"><p className="text-xs font-black uppercase tracking-wider text-slate-600">Payment Milestones</p><Button size="sm" variant="outline" onClick={addPayment}><Plus className="w-3 h-3 mr-1" />Add</Button></div>
                  {(q.payment_schedule || []).map((p, i) => (
                    <div key={i} className="grid grid-cols-[1fr_80px_120px_2fr_40px] gap-2 mb-1">
                      <Input value={p.label} onChange={(e) => setPayment(i, "label", e.target.value)} placeholder="Down Payment with PO" />
                      <Input type="number" value={p.percent ?? ""} onChange={(e) => setPayment(i, "percent", e.target.value === "" ? null : Number(e.target.value))} placeholder="40" />
                      <Input type="number" step="0.01" value={p.amount} onChange={(e) => setPayment(i, "amount", Number(e.target.value))} placeholder="0.00" />
                      <Input value={p.when} onChange={(e) => setPayment(i, "when", e.target.value)} placeholder="due upon Owner PO issuance" />
                      <Button size="icon" variant="ghost" onClick={() => removePayment(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  ))}
                </div>
              </Section>

              {/* ---------- TIMELINE + SIG ---------- */}
              <Section id="timeline" title="Timeline, Warranty & Signature" open={openSections.has("timeline")} onToggle={toggleSection}>
                <div>
                  <div className="flex justify-between items-center mb-1"><p className="text-xs font-black uppercase tracking-wider text-slate-600">Project Timeline Milestones</p><Button size="sm" variant="outline" onClick={addTimeline}><Plus className="w-3 h-3 mr-1" />Add</Button></div>
                  {(q.timeline || []).map((t, i) => (
                    <div key={i} className="grid grid-cols-[140px_1fr_40px] gap-2 mb-1">
                      <Input value={t.when} onChange={(e) => setTimelineRow(i, "when", e.target.value)} placeholder="Sep 2026" />
                      <Input value={t.description} onChange={(e) => setTimelineRow(i, "description", e.target.value)} placeholder="Contract execution — PO issued…" />
                      <Button size="icon" variant="ghost" onClick={() => removeTimeline(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  ))}
                </div>

                <Field label="Warranty summary"><Textarea rows={3} value={q.warranty_summary} onChange={(e) => set("warranty_summary", e.target.value)} /></Field>
                <Field label="Preventive maintenance summary"><Textarea rows={3} value={q.preventive_maintenance_summary} onChange={(e) => set("preventive_maintenance_summary", e.target.value)} /></Field>
                <Field label="Acceptance paragraph"><Textarea rows={3} value={q.acceptance_paragraph} onChange={(e) => set("acceptance_paragraph", e.target.value)} /></Field>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="Signer name"><Input value={q.signer_name} onChange={(e) => set("signer_name", e.target.value)} /></Field>
                  <Field label="Signer title"><Input value={q.signer_title} onChange={(e) => set("signer_title", e.target.value)} /></Field>
                  <Field label="Signer date"><Input value={q.signer_date} onChange={(e) => set("signer_date", e.target.value)} placeholder="September 9, 2026" /></Field>
                </div>
              </Section>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-slate-200 px-4 sm:px-5 py-3 bg-slate-50 rounded-b-xl flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {editingId && (
              <a href={`${API}/api/capital-quotes/${editingId}/preview`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-cyan-700 hover:underline" data-testid="cqb-preview-link">
                <Eye className="w-3.5 h-3.5" /> Preview current saved version
              </a>
            )}
            {editingId && (
              <a href={`${API}/api/capital-quotes/${editingId}/docx`} className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 hover:underline" data-testid="cqb-docx-link">
                <Eye className="w-3.5 h-3.5" /> Download Word (.docx)
              </a>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button variant="outline" onClick={onClose} data-testid="cqb-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700" data-testid="cqb-save">
              {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-1" />Save Quote</>}
            </Button>
          </div>
        </div>
    </Shell>
  );
}

// Stable module-level wrapper — defining this inside the component would
// remount the whole form on every keystroke.
function Shell({ pageMode, wide, children }) {
  if (pageMode) {
    return (
      <div className="w-full" data-testid="capital-quote-builder">
        <div className={`bg-white rounded-xl border border-slate-200 shadow-sm mx-auto ${wide ? "max-w-none" : "max-w-6xl"}`}>
          {children}
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="capital-quote-builder">
      <div className="bg-white w-full max-w-5xl rounded-xl shadow-2xl mt-4 mb-16">{children}</div>
    </div>
  );
}

function Section({ id, title, open, onToggle, children }) {
  return (
    <div className="rounded-lg border border-slate-200 mb-3 overflow-hidden" data-testid={`cqb-section-${id}`}>
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full flex items-center gap-2 px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-left"
        data-testid={`cqb-section-toggle-${id}`}
      >
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        <span className="text-sm font-bold text-slate-800">{title}</span>
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

function Field({ label, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[10px] font-black uppercase tracking-wider text-slate-600 block mb-0.5">{label}</span>
      {children}
    </label>
  );
}
